import React, { useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, GoogleAuthProvider, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup, signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { normalizeAdmin, SUPER_ADMIN_EMAILS } from '../adminAccess';
import { findBuiltInAdmin } from '../adminCredentials';

// Admin credentials (local auth — no Firebase needed)

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [resetting, setResetting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    await new Promise(r => setTimeout(r, 600)); // brief loading feel

    const emailLower = email.trim().toLowerCase();
    const builtInUser = findBuiltInAdmin(email, password);
    let user = null;

    try {
      const cred = await signInWithEmailAndPassword(auth, emailLower, password);
      if (builtInUser) {
        user = builtInUser;
      } else if (SUPER_ADMIN_EMAILS.includes(emailLower)) {
        // Super admins are authorised by email and need no admin_users document.
        user = {
          id: emailLower,
          email: emailLower,
          role: 'super_admin',
          name: cred.user.displayName || emailLower.split('@')[0],
        };
      } else {
        const docSnap = await getDoc(doc(db, 'admin_users', emailLower));
        if (docSnap.exists() && docSnap.data().status !== 'disabled') {
          user = { id: docSnap.id, ...docSnap.data() };
        }
      }
    } catch (err) {
      if (builtInUser) {
        try {
          await createUserWithEmailAndPassword(auth, emailLower, password);
          user = builtInUser;
        } catch (createErr) {
          console.error(createErr);
          setError(
            'Firebase Email/Password login is disabled or unavailable. Enable it in Firebase Authentication, then log in again.'
          );
          setLoading(false);
          return;
        }
      } else {
        console.error(err);
      }
    }

    // NOTE: a legacy fallback here compared a plaintext `password` field on
    // admin_users/<email>. Firestore rules require an authenticated panel admin
    // to read that collection, so the path could never succeed — and storing
    // plaintext passwords is unsafe regardless. Removed deliberately.

    if (!user) {
      // Credentials were valid but this account is not an admin → record a request.
      if (auth.currentUser && auth.currentUser.email?.toLowerCase() === emailLower) {
        await requestAccess(auth.currentUser, 'password');
      } else {
        setError('Email or password is incorrect, or this admin user is disabled.');
      }
      setLoading(false);
      return;
    }

    const { password: _pw, ...normalized } = normalizeAdmin(user);
    localStorage.setItem('admin_user', JSON.stringify(normalized));
    onLogin(normalized);
    setLoading(false);
  };

  // Records an access request for a signed-in user who is not yet an admin,
  // then drops the session. A super admin approves it from Admin access.
  async function requestAccess(fbUser, provider) {
    const emailLower = (fbUser.email || '').toLowerCase();
    try {
      await setDoc(doc(db, 'admin_access_requests', emailLower), {
        email: emailLower,
        name: fbUser.displayName || emailLower.split('@')[0],
        photoURL: fbUser.photoURL || '',
        provider,
        status: 'pending',
        requestedAt: serverTimestamp(),
      }, { merge: true });
      setNotice(`Access request sent for ${emailLower}. An administrator will review it — you'll be able to sign in once approved.`);
    } catch (err) {
      console.error(err);
      setError(`${emailLower} is not an authorised admin account, and the access request could not be saved.`);
    }
    try { await signOut(auth); } catch (e) { console.error(e); }
  }

  const handleGoogle = async () => {
    setError(''); setNotice(''); setGoogleLoading(true);
    let cred = null;
    try {
      cred = await signInWithPopup(auth, new GoogleAuthProvider());
      const emailLower = (cred.user.email || '').toLowerCase();
      if (!emailLower) throw new Error('no-email');

      let adminUser = null;
      if (SUPER_ADMIN_EMAILS.includes(emailLower)) {
        adminUser = { id: emailLower, email: emailLower, role: 'super_admin', name: cred.user.displayName || 'Super admin' };
      } else {
        const snap = await getDoc(doc(db, 'admin_users', emailLower));
        if (snap.exists() && snap.data().status !== 'disabled') {
          adminUser = { id: snap.id, ...snap.data() };
        }
      }

      if (!adminUser) {
        await requestAccess(cred.user, 'google');
        setGoogleLoading(false);
        return;
      }

      const { password: _pw, ...normalized } = normalizeAdmin(adminUser);
      localStorage.setItem('admin_user', JSON.stringify(normalized));
      onLogin(normalized);
    } catch (err) {
      console.error(err);
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        setError('');
      } else if (err?.code === 'auth/popup-blocked') {
        setError('Your browser blocked the sign-in popup. Allow popups for this site and try again.');
      } else {
        try { await signOut(auth); } catch (e2) { console.error(e2); }
        setError('Google sign-in failed. Please try again.');
      }
    }
    setGoogleLoading(false);
  };

  const handleReset = async () => {
    const emailLower = email.trim().toLowerCase();
    setError(''); setNotice('');
    if (!emailLower) { setError('Enter your email address first, then click "Forgot password?".'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailLower)) { setError('That does not look like a valid email address.'); return; }
    setResetting(true);
    try {
      await sendPasswordResetEmail(auth, emailLower);
      // Firebase does not reveal whether an account exists — keep the message neutral.
      setNotice(`If an account exists for ${emailLower}, a password reset link has been sent. Check your inbox and spam folder.`);
    } catch (err) {
      console.error(err);
      if (err?.code === 'auth/invalid-email') setError('That does not look like a valid email address.');
      else if (err?.code === 'auth/too-many-requests') setError('Too many attempts. Wait a few minutes and try again.');
      else setError('Could not send the reset email. Please try again.');
    }
    setResetting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm border border-gray-100">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
            <span className="text-3xl">🌿</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">PakMazar Admin Panel</h1>
          <p className="text-gray-500 text-sm mt-1">Log in to the admin panel</p>
        </div>

        <button
          type="button"
          onClick={handleGoogle}
          disabled={googleLoading}
          className="w-full border border-gray-300 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition flex items-center justify-center gap-2 mb-5"
        >
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-2.8-.4-4H24v7.3h12.1c-.2 2-1.6 5-4.5 7l-.1.3 6.5 5 .5.1c4.1-3.8 6.6-9.4 6.6-15.7"/>
            <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.3l-6.9-5.4c-1.8 1.3-4.3 2.2-7.6 2.2-5.8 0-10.7-3.8-12.5-9.1l-.3
 0-6.7 5.2-.1.3C7.4 40.9 15.1 46 24 46"/>
            <path fill="#FBBC05" d="M11.5 28.4c-.5-1.4-.7-2.9-.7-4.4s.3-3 .7-4.4v-.3l-6.8-5.3-.2.1C2.9 17.1 2 20.4 2 24s.9 6.9 2.5 9.9z"/>
            <path fill="#EA4335" d="M24 10.5c4.1 0 6.9 1.8 8.5 3.3l6.2-6C34.9 4.4 29.9 2 24 2 15.1 2 7.4 7.1 4.5 14.1l7 5.4c1.8-5.3 6.7-9 12.5-9"/>
          </svg>
          {googleLoading ? 'Signing in…' : 'Sign in with Google'}
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-xs text-gray-400">or</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              autoFocus
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
              >
                {showPass ? 'Hide' : 'Show'}
              </button>
            </div>
            <div className="text-right mt-1.5">
              <button
                type="button"
                onClick={handleReset}
                disabled={resetting}
                className="text-xs text-primary hover:underline disabled:opacity-50"
              >
                {resetting ? 'Sending…' : 'Forgot password?'}
              </button>
            </div>
          </div>

          {/* Notice */}
          {notice && (
            <div className="flex items-start gap-2 text-emerald-700 text-sm bg-emerald-50 px-3 py-2 rounded-lg">
              <span>✉️</span> <span>{notice}</span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-2.5 rounded-lg font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Logging in...
              </>
            ) : 'Log in'}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">
          PakMazar Admin Panel v1.0
        </p>
      </div>
    </div>
  );
}
